<template>
  <div>
    <flow-step-base
      name="Cancellation Survey"
      :guid="step.guid"
      :imgPath="img"
      description="Get insightful feedback from customers during the cancellation process."
      @disable="$emit('update', { enabled: false })"
    >
      <div class="mt-4">
        <p class="text-sm font-semibold mb-2 text-gray-700">What do you want this step to say?</p>
        <el-input
          type="text"
          placeholder="Headline"
          :value="step.header"
          @input="(header) => $emit('update', { header })"
          maxlength="80"
          show-word-limit
        >
        </el-input>
        <div style="margin: 20px 0"></div>
        <Editor :placeholder="step.description" :maxlength="0" @update="(description) => $emit('update', { description })" />
      </div>
      <div class="mt-4">
        <p class="text-sm font-semibold mb-2 text-gray-700 field-set-style text-left overflow-hidden">Survey Response Options</p>
        <draggable class="list-group" tag="ul" v-model="responseList" v-bind="dragOptions" @start="drag = true" @end="drag = false">
          <transition-group type="transition" :name="!drag ? 'flip-list' : null">
            <li class="list-group-item" v-for="(element, index) in responseList" :key="getChoiceKey(element)">
              <div>
                <div class="flex items-center pb-2 pt-4 pr-0">
                  <span
                    class="alphabet-list-icon w-8 font-bold bg-brand-sand leading-8 rounded-full align-middle block text-center text-brand-brown"
                    :class="{ 'alphabet-list-icon-after': element.followup }"
                    >{{ (index + 1 + 9).toString(36).toUpperCase() }}</span
                  >
                  <el-input class="pl-2" :value="element.value" @input="(text) => updateChoice(element, text)"></el-input>
                  <button @click="removeQuestion(element)" class="remove-survey-response absolute -right-3">
                    <img :src="require(`@/assets/img/cancel-circle.png`)" />
                  </button>
                </div>
                <button v-if="!element.followup" class="text-brand-brown font-semibold space-x-1.5 follow-up-cta ml-8">
                  <img class="inline-block" :src="require(`@/assets/img/followup-question.svg`)" />
                  <span @click="switchFollowup(element)" class="text-center text-xs font-semibold text-brand-brown">Add Follow-up question</span>
                </button>
                <div v-else class="flex items-center pb-2 pt-2 pr-0 follow-up-question rounded-lg relative">
                  <span class="follow-up-question-icon w-8 mr-2">
                    <img class="block m-auto" :src="require(`@/assets/img/followup-question.svg`)" />
                  </span>
                  <el-input
                    :placeholder="`Follow-up question for '${element.value}'`"
                    class="default-brand-border text-xs"
                    :value="element.followup.question"
                    type="textarea"
                    @input="(text) => updateFollowupQuestion(element, text)"
                    maxlength="100"
                    show-word-limit
                  ></el-input>
                  <button @click="switchFollowup(element)" class="cancel-follow-up-question absolute -right-3">
                    <img :src="require(`@/assets/img/cancel-circle.png`)" />
                  </button>
                </div>
              </div>
            </li>
          </transition-group>
        </draggable>
      </div>
      <div class="text-center mt-4">
        <el-button type="primary" @click="addResponse">+ Add Another Response</el-button>
      </div>
      <div v-if="true" class="mt-4 border-t-2 rounded-sm border-brand-sand">
        <div class="mt-4 space-y-2">
          <p class="text-sm font-semibold text-gray-700 flex-grow-0 flex-shrink-0">Randomize response order?</p>
          <el-switch
            :value="step.survey.randomize"
            @change="(randomize) => $emit('update', { randomize }, 'survey')"
            active-text="Yes"
            inactive-text="No"
          ></el-switch>
        </div>
        <div class="mt-4 space-y-2">
          <p class="text-sm font-semibold text-gray-700 flex-grow-0 flex-shrink-0">Follow-up response required?</p>
          <el-switch
            :value="step.survey.followupRequired"
            @change="(followupRequired) => $emit('update', { followupRequired, minLength: step.survey.minLength || 10 }, 'survey')"
            active-text="Yes"
            inactive-text="No"
          ></el-switch>
        </div>
        <el-collapse-transition>
          <div class="mt-4" v-show="step.survey.followupRequired">
            <p class="text-sm font-semibold mb-2 text-gray-700">Minimum character length</p>
            <el-input-number
              size="small"
              class="mt-1"
              :value="step.survey.minLength"
              :step="5"
              @change="(minLength) => $emit('update', { followupRequired: true, minLength }, 'survey')"
              :min="0"
              :max="400"
            >
            </el-input-number>
          </div>
        </el-collapse-transition>
      </div>
    </flow-step-base>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

import draggable from 'vuedraggable';
import FlowStepBase from '@/components/FlowSteps/FlowStepBase.vue';
import Editor from '@/components/UI/Editor.vue';
import img from '@/assets/img/icon-survey.svg';

export default {
  name: 'FlowStepCancelSurvey',
  components: {
    draggable,
    FlowStepBase,
    Editor,
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      drag: false,
      img,
    };
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    responseList: {
      get() {
        return this.step.survey.choices;
      },
      set(value) {
        this.$emit('update', { choices: value }, 'survey');
      },
    },
  },
  methods: {
    updateChoice(element, text) {
      const choices = this.step.survey.choices.map((d) => {
        if (d.guid === element.guid) {
          d.value = text;
        }
        return d;
      });
      this.$emit('update', { choices }, 'survey');
    },
    addResponse() {
      const responses = [
        ...this.step.survey.choices,
        {
          value: 'New Response',
          type: 'RADIO',
          guid: uuidv4(),
        },
      ];
      this.$emit('update', { choices: responses }, 'survey');
    },
    getChoiceKey(choice) {
      return choice.guid || choice._id;
    },
    removeQuestion(element) {
      const responses = [...this.step.survey.choices.filter((d) => this.getChoiceKey(element) !== this.getChoiceKey(d))];
      this.$emit('update', { choices: responses }, 'survey');
    },
    updateFollowupQuestion(element, text) {
      const choices = this.step.survey.choices.map((d) => {
        if (d.guid === element.guid) {
          d.followup.question = text;
        }
        return d;
      });
      this.$emit('update', { choices }, 'survey');
    },
    switchFollowup(element) {
      const choices = this.step.survey.choices.map((d) => {
        if (d.guid === element.guid) {
          if (d.followup) {
            delete d.followup;
            d.type = 'RADIO';
          } else {
            d.followup = { question: '' };
            d.type = 'INPUT';
          }
        }
        return d;
      });
      this.$emit('update', { choices }, 'survey');
    },
  },
};
</script>

<style lang="scss" scoped>
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
}
.list-group-item {
  cursor: move;
  position: relative;
}
.list-group-item i {
  cursor: pointer;
}
.alphabet-list-icon-after:after {
  content: '';
  width: 2px;
  border: 2px solid #f7efe6;
  position: absolute;
  top: 48px;
  height: 41px;
  left: 13px;
}
</style>
